import { useEffect, useMemo, useState, useLayoutEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Question, ArrowLeft, ArrowRight, X, Checks } from '@phosphor-icons/react';
import { getTourForRole, getMiniTour } from './TourRegistry';
import api from '../api';

// ---------- Helpers ----------
const REPORT_EVENT = async (tour_id, event, extras = {}) => {
  try { await api.put('/tours/progress', { tour_id, event, ...extras }); } catch {}
};

async function waitForSelector(sel, timeout = 2500) {
  if (!sel) return null;
  const t0 = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const el = document.querySelector(sel);
      if (el) return resolve(el);
      if (Date.now() - t0 > timeout) return resolve(null);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function computePosition(rect, cardW, cardH, placement) {
  const margin = 12;
  const vw = window.innerWidth, vh = window.innerHeight;
  const spaces = {
    top: rect.top,
    bottom: vh - rect.bottom,
    left: rect.left,
    right: vw - rect.right,
  };
  let p = placement;
  if (p === 'auto' || !p) {
    p = Object.entries(spaces).sort((a, b) => b[1] - a[1])[0][0];
  }
  let top = 0, left = 0;
  if (p === 'top')    { top = rect.top - cardH - margin; left = rect.left + rect.width / 2 - cardW / 2; }
  if (p === 'bottom') { top = rect.bottom + margin;      left = rect.left + rect.width / 2 - cardW / 2; }
  if (p === 'left')   { top = rect.top + rect.height / 2 - cardH / 2; left = rect.left - cardW - margin; }
  if (p === 'right')  { top = rect.top + rect.height / 2 - cardH / 2; left = rect.right + margin; }
  // Clamp to viewport
  top = Math.max(margin, Math.min(top, vh - cardH - margin));
  left = Math.max(margin, Math.min(left, vw - cardW - margin));
  return { top, left, placement: p };
}

// ---------- Component ----------
export default function InteractiveTour({ isOpen, onClose, tourId = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'alumno';
  const tour = useMemo(() => (tourId ? (getMiniTour(tourId) || getTourForRole(role)) : getTourForRole(role)), [role, tourId]);

  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const step = tour.steps[stepIdx];

  // Recompute rect for the target element
  const refresh = useCallback(async () => {
    if (!step || !step.target) { setRect(null); return; }
    // v10.4.6: si el step requiere abrir un menú padre (sidebar collapsible),
    // disparar `tour:open-group` que AppLayout escucha para expandirlo.
    if (step.group) {
      window.dispatchEvent(new CustomEvent('tour:open-group', { detail: { group: step.group } }));
      await new Promise((r) => setTimeout(r, 500)); // esperar animación de expand
    }
    // v10.4.6: si el step requiere activar una pestaña o pre-clicar algo
    // (ej. tabs dentro de la simulación, sub-menús), buscar el elemento
    // y hacer click sintético. Idempotente y silencioso si ya está activo.
    if (step.preClick) {
      const preEl = await waitForSelector(step.preClick, 1500);
      if (preEl) {
        try {
          // Radix Tabs y componentes accesibles responden a click nativo.
          preEl.click();
          await new Promise((r) => setTimeout(r, 350));
        } catch (_) { /* silencioso */ }
      }
    }
    const el = await waitForSelector(step.target, 2500);
    if (!el) { setRect(null); return; }
    // Scroll into view if needed
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    // Wait a tick for scroll to settle
    setTimeout(() => setRect(el.getBoundingClientRect()), 250);
  }, [step]);

  // Handle step change: navigate if route, then refresh
  useEffect(() => {
    if (!isOpen || showWelcome) return;
    (async () => {
      if (step?.route && window.location.pathname !== step.route) {
        navigate(step.route);
        // small delay for the route render
        await new Promise(r => setTimeout(r, 400));
      }
      await refresh();
      REPORT_EVENT(tour.id, 'step_viewed', { current_step: stepIdx, total_steps: tour.steps.length });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, isOpen, showWelcome]);

  // Keep rect fresh on scroll/resize
  useLayoutEffect(() => {
    if (!isOpen || showWelcome) return;
    const handler = () => refresh();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isOpen, showWelcome, refresh]);

  // Keyboard controls
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, stepIdx, showWelcome]);

  // Report started when tour opens
  useEffect(() => {
    if (isOpen) REPORT_EVENT(tour.id, 'started', { tour_version: tour.version });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const next = () => {
    if (showWelcome) { setShowWelcome(false); return; }
    if (stepIdx < tour.steps.length - 1) setStepIdx(stepIdx + 1);
    else complete();
  };
  const prev = () => {
    if (showWelcome) return;
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const complete = async () => {
    await REPORT_EVENT(tour.id, 'completed', {
      current_step: tour.steps.length - 1,
      total_steps: tour.steps.length,
      completed: true,
      tour_version: tour.version,
    });
    onClose?.();
  };

  const handleClose = async () => {
    // If not on last step and not completed, mark as skipped
    if (stepIdx < tour.steps.length - 1) {
      await REPORT_EVENT(tour.id, 'skipped', {
        current_step: stepIdx,
        total_steps: tour.steps.length,
        skipped: true,
        tour_version: tour.version,
      });
    }
    onClose?.();
  };

  const dontShowAgain = async () => {
    await api.put('/tours/progress', {
      tour_id: tour.id,
      dont_show_again: true,
      skipped: true,
      event: 'skipped',
      tour_version: tour.version,
    }).catch(() => {});
    onClose?.();
  };

  // ---- Welcome modal (spec §2) ----
  if (showWelcome) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="tour-welcome">
        <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
              <Question size={22} className="text-cyan-600" weight="bold" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Bienvenido a SimuClinic</h3>
              <p className="text-xs text-slate-500">Duración estimada: 5-7 minutos</p>
            </div>
          </div>
          <p className="text-sm text-slate-600">
            Te acompañaremos en un recorrido rápido por las principales funcionalidades de la plataforma para tu rol de <strong>{role}</strong>.
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            <Button onClick={next} data-testid="tour-start-btn">Comenzar recorrido</Button>
            <Button variant="outline" onClick={handleClose} data-testid="tour-later-btn">Ver más tarde</Button>
            <Button variant="ghost" size="sm" onClick={dontShowAgain} className="ml-auto text-xs" data-testid="tour-never-btn">
              No volver a mostrar
            </Button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ---- Spotlight + Tooltip ----
  const cardW = 340, cardH = 210;
  const centered = !rect;
  const pos = centered
    ? { top: window.innerHeight / 2 - cardH / 2, left: window.innerWidth / 2 - cardW / 2, placement: 'center' }
    : computePosition(rect, cardW, cardH, step.placement || 'auto');
  const stepNum = stepIdx + 1;
  const total = tour.steps.length;
  const isLast = stepIdx === total - 1;

  return createPortal(
    <div className="fixed inset-0 z-[9998]" data-testid="tour-overlay">
      {/* Dim overlay with cutout for the target */}
      <svg width="100%" height="100%" className="absolute inset-0 pointer-events-auto"
           onClick={handleClose}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(15,23,42,0.55)" mask="url(#tour-mask)" />
        {rect && (
          <rect
            x={rect.left - 6}
            y={rect.top - 6}
            width={rect.width + 12}
            height={rect.height + 12}
            rx="8"
            fill="none"
            stroke="#06b6d4"
            strokeWidth="2"
            className="tour-pulse"
          />
        )}
      </svg>

      {/* Tooltip card */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl border border-slate-200 pointer-events-auto"
        style={{ top: pos.top, left: pos.left, width: cardW, minHeight: 120 }}
        data-testid="tour-card"
        role="dialog"
        aria-label={step.title}
      >
        <div className="p-4">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h4 className="text-sm font-semibold text-slate-900 flex-1">{step.title}</h4>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-600"
              aria-label="Cerrar recorrido"
              data-testid="tour-close-btn"
            >
              <X size={16} />
            </button>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">{step.body}</p>
          <div className="flex items-center justify-between mt-4">
            <span className="text-[11px] text-slate-400">Paso {stepNum} de {total}</span>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={prev}
                disabled={stepIdx === 0}
                className="h-7 px-2 text-xs"
                data-testid="tour-prev-btn"
              >
                <ArrowLeft size={12} className="mr-1" />
                Anterior
              </Button>
              <Button
                size="sm"
                onClick={next}
                className="h-7 px-2 text-xs"
                data-testid="tour-next-btn"
              >
                {isLast ? (<><Checks size={12} className="mr-1" />Finalizar</>) : (<>Siguiente<ArrowRight size={12} className="ml-1" /></>)}
              </Button>
            </div>
          </div>
          {rect === null && step.target && (
            <p className="text-[10px] text-amber-600 mt-2">
              (No encontramos este elemento en la pantalla actual — el recorrido continúa)
            </p>
          )}
        </div>
      </div>

      <style>{`
        .tour-pulse {
          animation: tour-pulse 2s ease-in-out infinite;
        }
        @keyframes tour-pulse {
          0%, 100% { stroke-opacity: 1; }
          50% { stroke-opacity: 0.4; }
        }
      `}</style>
    </div>,
    document.body
  );
}

// ---------- Public helpers (compat with AppLayout wiring) ----------

export function TourHelpButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors"
      title="Ayuda / Recorrido"
      aria-label="Ayuda"
      data-testid="tour-help-btn"
    >
      <Question size={18} weight="bold" />
    </button>
  );
}

/**
 * useTour — decides whether to auto-launch the tour based on backend progress.
 * Shows the tour only when the user has NEVER completed nor set "don't show again".
 */
export function useTour() {
  const { user } = useAuth();
  const [tourOpen, setTourOpen] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user || checked) return;
    (async () => {
      try {
        const resp = await api.get('/tours/progress');
        const all = resp.data?.progress || {};
        const tour = getTourForRole(user.role);
        const p = all[tour.id];
        // Auto-open only if never seen and not dismissed
        if (!p || (!p.completed && !p.dont_show_again && !p.skipped)) {
          // small delay so the layout finishes mounting
          setTimeout(() => setTourOpen(true), 800);
        }
      } catch {
        // If backend fails silently, don't auto-open — user can trigger manually
      } finally {
        setChecked(true);
      }
    })();
  }, [user, checked]);

  return {
    isOpen: tourOpen,
    tourOpen,
    openTour: () => setTourOpen(true),
    closeTour: () => setTourOpen(false),
  };
}
